import React, { useState, useEffect } from "react";
import { PrimaryBlueButton, SecondaryBlueButton, IconTrash, Severity } from "@velaro/velaro-component-lib";
import { CreateScraperJobRequest, ProbeRecommendation, ProbeResult } from "./types";
import useToastAlert from "@/hooks/useToastAlert";
import useApi from "@/hooks/useApi";

interface UrlSuggestion {
  setting: keyof CreateScraperJobRequest | "showAdvanced" | "sitemap" | "jscode";
  label: string;
  reason: string;
  action?: () => void;
}

function analyzeUrls(urls: string[]): UrlSuggestion[] {
  if (urls.length === 0) return [];

  const suggestions: UrlSuggestion[] = [];
  const seen = new Set<string>();

  const add = (id: string, s: Omit<UrlSuggestion, "setting"> & { setting: UrlSuggestion["setting"] }) => {
    if (!seen.has(id)) { seen.add(id); suggestions.push(s); }
  };

  for (const raw of urls) {
    let hostname = "";
    let path = "";
    try { const u = new URL(raw); hostname = u.hostname.toLowerCase(); path = u.pathname.toLowerCase(); }
    catch { continue; }

    // EU / privacy-heavy TLDs — consent popups almost certain
    if (/\.(de|fr|nl|it|es|pl|be|at|dk|se|no|fi|ch|pt|ie|eu)$/.test(hostname)) {
      add("consent", {
        setting: "removeConsentPopups",
        label: "Auto-dismiss consent banners",
        reason: `${hostname} is on a European TLD — GDPR consent popups typically block content extraction.`
      });
    }

    // Known e-commerce / product catalog platforms
    if (hostname.includes("shopify.com") || hostname.includes("myshopify.com") ||
        path.includes("/products/") || path.includes("/collections/")) {
      add("shopify", {
        setting: "scrollFullPage",
        label: "Scroll full page",
        reason: "Shopify stores lazy-load reviews, variant options, and cross-sells below the fold."
      });
    }

    if (path.includes("/catalog") || path.includes("/shop") || path.includes("/store") ||
        path.includes("/product") || path.includes("/item")) {
      add("catalog", {
        setting: "scrollFullPage",
        label: "Scroll full page",
        reason: "Product and catalog pages often lazy-load specs, reviews, and compatibility tables."
      });
    }

    // Automotive / parts / fitment
    if (path.includes("/parts") || path.includes("/fitment") || path.includes("/compatibility") ||
        hostname.includes("autozone") || hostname.includes("rockauto") || hostname.includes("partsgeek") ||
        hostname.includes("toyota") || hostname.includes("ford") || hostname.includes("gm.com") ||
        path.includes("/make/") || path.includes("/model/") || path.includes("/year/")) {
      add("automotive", {
        setting: "sitemap",
        label: "Check sitemap first",
        reason: "Parts/automotive catalog sites put every product at its own URL. Scraping from sitemap.xml discovers far more pages than depth crawling — try adding /sitemap.xml or /sitemap_index.xml as a URL."
      });
      add("autoScroll", {
        setting: "scrollFullPage",
        label: "Scroll full page",
        reason: "Compatibility tables and fitment grids on parts pages often load after the initial render."
      });
    }

    // Single-page apps — hash routing
    if (raw.includes("#/") || raw.includes("/#")) {
      add("spa-hash", {
        setting: "pageLoadDelay",
        label: "Add 2–3s page load delay",
        reason: "Hash-based routing (#/) means this is a single-page app. Content renders after JS runs — a delay ensures the DOM is populated before extraction."
      });
    }

    // Known SPA / CMS platforms
    if (hostname.includes("webflow.io") || hostname.includes("squarespace.com") ||
        path.includes("/app/") || path.includes("/dashboard/")) {
      add("spa-generic", {
        setting: "pageLoadDelay",
        label: "Add 1–2s page load delay",
        reason: `${hostname} appears to be a JavaScript-rendered site. A short delay after load improves content capture.`
      });
    }

    // Iframe-heavy embeds
    if (hostname.includes("zendesk") || hostname.includes("freshdesk") || hostname.includes("intercom") ||
        hostname.includes("helpscout") || hostname.includes("notion.site")) {
      add("iframe", {
        setting: "processIframes",
        label: "Process iframes",
        reason: "Help desk and documentation sites often embed content in iframes. Enabling this captures the embedded text."
      });
    }

    // Infinite scroll / blog / news
    if (path.includes("/blog") || path.includes("/news") || path.includes("/articles") ||
        hostname.includes("medium.com") || hostname.includes("substack.com")) {
      add("scroll-blog", {
        setting: "scrollFullPage",
        label: "Scroll full page",
        reason: "Blog and news sites typically lazy-load the article body and related posts."
      });
    }
  }

  return suggestions;
}

interface CreateScraperJobModalProps {
  onClose: () => void;
  onCreate: (request: CreateScraperJobRequest) => void;
}

export default function CreateScraperJobModal({
  onClose,
  onCreate
}: CreateScraperJobModalProps) {
  const [name, setName] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [urls, setUrls] = useState<string[]>([]);
  const [crawlDepth, setCrawlDepth] = useState(2);
  const [maxPages, setMaxPages] = useState(50);
  const [includeExternal, setIncludeExternal] = useState(false);
  const [crawlResultDelay, setCrawlResultDelay] = useState(1);
  const [language, setLanguage] = useState("english");
  const [excludeNavigationUrls, setExcludeNavigationUrls] = useState(true);
  const [crawlUrlExclusion, setCrawlUrlExclusion] = useState("");
  const [smartScan, setSmartScan] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [waitFor, setWaitFor] = useState("");
  const [pageLoadDelay, setPageLoadDelay] = useState(0);
  const [scrollFullPage, setScrollFullPage] = useState(false);
  const [removeConsentPopups, setRemoveConsentPopups] = useState(false);
  const [processIframes, setProcessIframes] = useState(false);
  const [stealthMode, setStealthMode] = useState(true);
  const [suggestions, setSuggestions] = useState<UrlSuggestion[]>([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const [isProbing, setIsProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);
  const [appliedProbeRecs, setAppliedProbeRecs] = useState<Set<string>>(new Set());
  const toastAlert = useToastAlert();
  const api = useApi();

  useEffect(() => {
    setSuggestions(analyzeUrls(urls));
  }, [urls]);

  const applySuggestion = (s: UrlSuggestion) => {
    if (s.setting === "scrollFullPage") { setScrollFullPage(true); setShowAdvanced(true); }
    else if (s.setting === "removeConsentPopups") { setRemoveConsentPopups(true); setShowAdvanced(true); }
    else if (s.setting === "processIframes") { setProcessIframes(true); setShowAdvanced(true); }
    else if (s.setting === "pageLoadDelay") { setPageLoadDelay(2); setShowAdvanced(true); }
    dismissSuggestion(s.label);
  };

  const dismissSuggestion = (label: string) => {
    setDismissedSuggestions(prev => new Set([...prev, label]));
  };

  const visibleSuggestions = suggestions.filter(s => !dismissedSuggestions.has(s.label));

  const runProbe = async () => {
    if (urls.length === 0) return;
    setIsProbing(true);
    setProbeResult(null);
    setAppliedProbeRecs(new Set());
    try {
      const res = await api.admin.post("ScraperJobs/Probe", { urls });
      if (!res.ok) throw new Error(await res.text());
      const data: ProbeResult = await res.json();
      setProbeResult(data);
      // Auto-apply high-confidence recommendations
      const autoApply = (data?.recommendations ?? []).filter(r => r.auto_enable);
      if (autoApply.length > 0) {
        applyProbeRecs(autoApply);
        setAppliedProbeRecs(new Set(autoApply.map(r => r.setting)));
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Site scan failed — check your URLs and try again.");
    } finally {
      setIsProbing(false);
    }
  };

  const applyProbeRecs = (recs: ProbeRecommendation[]) => {
    for (const r of recs) {
      if (r.setting === "scrollFullPage") { setScrollFullPage(true); setShowAdvanced(true); }
      else if (r.setting === "removeConsentPopups") { setRemoveConsentPopups(true); setShowAdvanced(true); }
      else if (r.setting === "processIframes") { setProcessIframes(true); setShowAdvanced(true); }
      else if (r.setting === "pageLoadDelay") { setPageLoadDelay(r.value ?? 3); setShowAdvanced(true); }
      else if (r.setting === "crawlResultDelay") { setCrawlResultDelay(r.value ?? 3); setShowAdvanced(true); }
    }
    setAppliedProbeRecs(prev => new Set([...prev, ...recs.map(r => r.setting)]));
  };

  const applyAllProbeRecs = () => {
    if (!probeResult) return;
    applyProbeRecs(probeResult.recommendations.filter(r => r.setting !== "_info"));
  };

  const VIDEO_DOMAINS = ["youtube.com", "youtu.be", "vimeo.com", "dailymotion.com", "twitch.tv", "tiktok.com"];

  const isVideoUrl = (url: string) => {
    const lower = url.toLowerCase();
    return VIDEO_DOMAINS.some(domain => lower.includes(domain));
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;

    // Basic URL validation
    try {
      new URL(urlInput);
    } catch {
      toastAlert.displayToast(Severity.Error, "Please enter a valid URL");
      return;
    }

    if (urls.includes(urlInput)) {
      toastAlert.displayToast(Severity.Warning, "URL already in list");
      return;
    }

    if (isVideoUrl(urlInput)) {
      toastAlert.displayToast(
        Severity.Warning,
        "This looks like a video URL. To extract video transcripts/captions, use the Video Training tab instead."
      );
    }

    setUrls([...urls, urlInput]);
    setUrlInput("");
  };

  const removeUrl = (url: string) => {
    setUrls(urls.filter(u => u !== url));
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toastAlert.displayToast(Severity.Error, "Please enter a job name");
      return;
    }
    if (urls.length === 0) {
      toastAlert.displayToast(Severity.Error, "Please add at least one URL");
      return;
    }

    const request: CreateScraperJobRequest = {
      name: name.trim(),
      urls,
      crawlDepth,
      maxPages,
      includeExternal,
      crawlResultDelay,
      language,
      excludeNavigationUrls,
      crawlUrlExclusion: crawlUrlExclusion.trim() || undefined,
      smartScan,
      waitFor: waitFor.trim() || undefined,
      pageLoadDelay: pageLoadDelay > 0 ? pageLoadDelay : undefined,
      scrollFullPage: scrollFullPage || undefined,
      removeConsentPopups: removeConsentPopups || undefined,
      processIframes: processIframes || undefined,
      stealthMode,
    };

    onCreate(request);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Create New Scraper Job</h2>
          <p className="text-sm text-slate-500">Configure web scraping settings</p>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Job Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Job Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Company Website Scrape"
              className="w-full p-2 border rounded-md text-sm"
            />
          </div>

          {/* URLs */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              URLs to Scrape *
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addUrl()}
                placeholder="https://example.com"
                className="flex-1 p-2 border rounded-md text-sm"
              />
              <SecondaryBlueButton label="Add" onClick={addUrl} />
            </div>
            {urls.length > 0 && (
              <div className="mt-2 space-y-1">
                {urls.map((url) => (
                  <div
                    key={url}
                    className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                  >
                    <span className="truncate">{url}</span>
                    <button
                      onClick={() => removeUrl(url)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <IconTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Smart Detect — scan actual pages for rendering issues */}
          {urls.length > 0 && (
            <div>
              {!probeResult && !isProbing && (
                <button
                  onClick={runProbe}
                  className="flex items-center gap-2 text-sm px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100"
                >
                  <span>🔍</span>
                  Smart Detect — scan site before scraping
                  <span className="text-xs text-blue-500">(~20s)</span>
                </button>
              )}
              {isProbing && (
                <div className="flex items-center gap-2 text-sm text-slate-500 p-3 bg-slate-50 rounded-lg border">
                  <svg className="animate-spin w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Scanning {Math.min(urls.length, 5)} page{Math.min(urls.length, 5) > 1 ? "s" : ""}… detecting rendering issues…
                </div>
              )}
              {probeResult && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-blue-900">
                      Scanned {probeResult.pages_probed} page{probeResult.pages_probed !== 1 ? "s" : ""}
                      {probeResult.avg_chars !== undefined && (
                        <span className="font-normal text-blue-700 ml-1">
                          — avg {probeResult.avg_chars.toLocaleString()} chars/page
                          {probeResult.avg_chars < 300 && <span className="text-amber-600 ml-1">(thin — JS likely not finishing)</span>}
                        </span>
                      )}
                    </div>
                    <button onClick={runProbe} className="text-xs text-blue-500 hover:text-blue-700">Re-scan</button>
                  </div>

                  {probeResult.error && (
                    <p className="text-sm text-red-600">{probeResult.error}</p>
                  )}

                  {probeResult.recommendations.length === 0 && !probeResult.error && (
                    <p className="text-sm text-green-700">No issues detected — site looks straightforward to scrape.</p>
                  )}

                  {probeResult.recommendations.length > 0 && (
                    <div className="space-y-2">
                      {probeResult.recommendations.map((r) => {
                        const isApplied = appliedProbeRecs.has(r.setting);
                        const isInfo = r.setting === "_info";
                        return (
                          <div key={r.setting} className={`flex items-start gap-2 p-2 rounded text-sm ${isInfo ? "bg-orange-50 border border-orange-200" : "bg-white border border-blue-100"}`}>
                            <span className="shrink-0 mt-0.5">
                              {isInfo ? "⚠️" : isApplied ? "✅" : r.confidence === "high" ? "🔴" : "🟡"}
                            </span>
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{r.label}</span>
                              <span className={`ml-1 text-xs px-1 rounded ${r.confidence === "high" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                                {r.confidence}
                              </span>
                              <p className="text-slate-600 text-xs mt-0.5">{r.reason}</p>
                            </div>
                            {!isInfo && !isApplied && (
                              <button
                                onClick={() => applyProbeRecs([r])}
                                className="text-xs px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:opacity-90 shrink-0"
                              >
                                Apply
                              </button>
                            )}
                          </div>
                        );
                      })}
                      {probeResult.recommendations.filter(r => r.setting !== "_info" && !appliedProbeRecs.has(r.setting)).length > 1 && (
                        <button
                          onClick={applyAllProbeRecs}
                          className="text-xs px-3 py-1.5 bg-[var(--color-primary)] text-white rounded hover:opacity-90 w-full"
                        >
                          Apply All Recommendations
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* URL-pattern suggestions (heuristic, no scan needed) */}
          {visibleSuggestions.length > 0 && (
            <div className="space-y-2">
              {visibleSuggestions.map((s) => (
                <div key={s.label} className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <span className="text-amber-500 mt-0.5 shrink-0">💡</span>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-amber-900">{s.label}:</span>
                    <span className="text-amber-800 ml-1">{s.reason}</span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {s.setting !== "sitemap" && s.setting !== "jscode" && (
                      <button
                        onClick={() => applySuggestion(s)}
                        className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700"
                      >
                        Enable
                      </button>
                    )}
                    <button
                      onClick={() => dismissSuggestion(s.label)}
                      className="text-xs px-2 py-1 text-amber-600 hover:text-amber-800"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Basic Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Crawl Depth
              </label>
              <select
                value={crawlDepth}
                onChange={(e) => setCrawlDepth(Number(e.target.value))}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value={1}>1 - Start page only</option>
                <option value={2}>2 - Links from start page</option>
                <option value={3}>3 - Two levels deep</option>
                <option value={4}>4 - Three levels deep</option>
                <option value={5}>5 - Four levels deep</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Max Pages
              </label>
              <select
                value={maxPages}
                onChange={(e) => setMaxPages(Number(e.target.value))}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value={25}>25 pages</option>
                <option value={50}>50 pages</option>
                <option value={100}>100 pages</option>
                <option value={200}>200 pages</option>
                <option value={500}>500 pages</option>
              </select>
            </div>
          </div>

          {/* Language Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Language Filter
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full p-2 border rounded-md text-sm"
            >
              <option value="english">English</option>
              <option value="spanish">Spanish</option>
              <option value="french">French</option>
              <option value="">No filter</option>
            </select>
          </div>

          {/* Checkboxes */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={excludeNavigationUrls}
                onChange={(e) => setExcludeNavigationUrls(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Exclude navigation URLs (login, cart, search, etc.)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeExternal}
                onChange={(e) => setIncludeExternal(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Include external links</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={smartScan}
                onChange={(e) => setSmartScan(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">
                Smart Scan — skip pages that haven't changed since the last run
                <span className="ml-1 text-slate-400">(faster, saves quota)</span>
              </span>
            </label>
          </div>

          {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showAdvanced ? "Hide" : "Show"} Advanced Settings
          </button>

          {/* Advanced Settings */}
          {showAdvanced && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Delay Between Requests (seconds)
                </label>
                <input
                  type="number"
                  value={crawlResultDelay}
                  onChange={(e) => setCrawlResultDelay(Number(e.target.value))}
                  min={0}
                  max={10}
                  className="w-full p-2 border rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  URL Exclusion Patterns (comma-separated)
                </label>
                <input
                  type="text"
                  value={crawlUrlExclusion}
                  onChange={(e) => setCrawlUrlExclusion(e.target.value)}
                  placeholder="/admin, /private, .pdf"
                  className="w-full p-2 border rounded-md text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">
                  URLs containing these patterns will be skipped
                </p>
              </div>

              {/* Stealth mode */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Anti-Bot Protection
                </p>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={stealthMode}
                    onChange={(e) => setStealthMode(e.target.checked)}
                    className="rounded mt-0.5"
                  />
                  <span className="text-sm">
                    Stealth Mode
                    <span className="block text-xs text-slate-400">
                      Bypasses Cloudflare, ModSecurity, and other bot detection systems by spoofing browser fingerprints. Automatically retries blocked pages. Recommended for most sites.
                    </span>
                  </span>
                </label>
              </div>

              {/* Dynamic content options */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Dynamic Content
                </p>

                <div className="space-y-3">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={scrollFullPage}
                      onChange={(e) => setScrollFullPage(e.target.checked)}
                      className="rounded mt-0.5"
                    />
                    <span className="text-sm">
                      Scroll full page before extracting
                      <span className="block text-xs text-slate-400">For lazy-loaded content and infinite-scroll pages</span>
                    </span>
                  </label>

                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={removeConsentPopups}
                      onChange={(e) => setRemoveConsentPopups(e.target.checked)}
                      className="rounded mt-0.5"
                    />
                    <span className="text-sm">
                      Auto-dismiss cookie/consent banners
                      <span className="block text-xs text-slate-400">Removes GDPR popups that block content</span>
                    </span>
                  </label>

                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={processIframes}
                      onChange={(e) => setProcessIframes(e.target.checked)}
                      className="rounded mt-0.5"
                    />
                    <span className="text-sm">
                      Extract content from iframes
                      <span className="block text-xs text-slate-400">Useful for embedded help widgets or docs</span>
                    </span>
                  </label>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Wait for element before extracting
                  </label>
                  <input
                    type="text"
                    value={waitFor}
                    onChange={(e) => setWaitFor(e.target.value)}
                    placeholder="css:.product-details  or  js:() => !!document.querySelector('.price')"
                    className="w-full p-2 border rounded-md text-sm font-mono"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Prefix with <code>css:</code> for a CSS selector or <code>js:</code> for a JS expression. Useful when content loads after page render (SPAs, React apps).
                  </p>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Extra page load delay (seconds)
                  </label>
                  <input
                    type="number"
                    value={pageLoadDelay}
                    onChange={(e) => setPageLoadDelay(Number(e.target.value))}
                    min={0}
                    max={30}
                    step={0.5}
                    className="w-full p-2 border rounded-md text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Additional wait after page load. Try 2–5s for sites where content renders after an API call.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2">
          <SecondaryBlueButton label="Cancel" onClick={onClose} />
          <PrimaryBlueButton label="Create Job" onClick={handleSubmit} />
        </div>
      </div>
    </div>
  );
}
